import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { type EmbedProbeStatus, probeIframeEmbeddability } from '@/lib/iframe/can-embed';
import { isVitest } from '@/lib/test-env';
import type { EmbeddableStatus } from '@/lib/url-metadata/embeddability';
import { canEmbedHost } from '../utils';

export interface UseLinkPreviewViewOptions {
  defaultView: 'preview' | 'iframe';
  initialEmbeddableStatus: EmbeddableStatus;
  isExcluded: boolean;
  url: string;
}

export interface LinkPreviewViewState {
  applyEmbedStatus: (status: EmbeddableStatus) => void;
  closeIframeView: () => void;
  currentHistoryIndex: number;
  currentIframeTitle: string;
  embedSupport: EmbedProbeStatus | 'unknown';
  handleIframeBack: () => void;
  handleIframeError: () => void;
  handleIframeLoad: (title?: string) => void;
  iframeError: boolean;
  iframeHistory: string[];
  iframeUrl: string | null;
  imageError: boolean;
  isFullscreen: boolean;
  isIframeLoading: boolean;
  markImageError: () => void;
  openIframeView: () => void;
  toggleFullscreen: () => void;
  triggerProbeIfNeeded: () => void;
  viewMode: 'preview' | 'iframe';
}

function getHistoryEntry(history: readonly string[], index: number): string | null {
  if (!Number.isInteger(index)) {
    return null;
  }
  if (index < 0 || index >= history.length) {
    return null;
  }
  return history[index] ?? null;
}

export function useLinkPreviewViewState({
  url,
  defaultView,
  isExcluded,
  initialEmbeddableStatus,
}: UseLinkPreviewViewOptions): LinkPreviewViewState {
  const [imageError, setImageError] = useState(false);
  const [viewMode, setViewMode] = useState<'preview' | 'iframe'>(defaultView);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [iframeError, setIframeError] = useState(false);
  const [isIframeLoading, setIsIframeLoading] = useState(false);
  const [iframeHistory, setIframeHistory] = useState<string[]>([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentIframeTitle, setCurrentIframeTitle] = useState('');
  const [embedSupport, setEmbedSupport] = useState<EmbedProbeStatus | 'unknown'>('unknown');

  const iframeHistoryReference = useRef<string[]>([]);
  useLayoutEffect(() => {
    iframeHistoryReference.current = iframeHistory;
  }, [iframeHistory]);

  const probeCancelledReference = useRef(false);
  const hasProbedReference = useRef(false);
  const previousViewModeReference = useRef(viewMode);
  const cleanupRafReference = useRef<number | null>(null);

  const applyEmbedStatus = useCallback((status: EmbeddableStatus) => {
    if (status === 'deny') {
      setEmbedSupport('blocked');
      hasProbedReference.current = true;
      return;
    }

    setEmbedSupport('unknown');
    hasProbedReference.current = false;
  }, []);

  useLayoutEffect(() => {
    applyEmbedStatus(initialEmbeddableStatus);
  }, [initialEmbeddableStatus, applyEmbedStatus]);

  useLayoutEffect(() => {
    const shouldShowIframe = defaultView === 'iframe';
    setImageError(false);
    setViewMode(defaultView);
    const canAutoload = shouldShowIframe && initialEmbeddableStatus !== 'deny';
    setIframeUrl(canAutoload ? url : null);
    setIframeError(false);
    setIsIframeLoading(canAutoload);
    setIframeHistory(canAutoload && url ? [url] : []);
    setCurrentHistoryIndex(canAutoload && url ? 0 : -1);
    setIsFullscreen(false);
    setCurrentIframeTitle('');
    probeCancelledReference.current = false;
    hasProbedReference.current = initialEmbeddableStatus !== 'unknown';
  }, [defaultView, url, initialEmbeddableStatus]);

  useLayoutEffect(() => {
    if (viewMode !== 'iframe') {
      return;
    }
    if (embedSupport === 'blocked') {
      setIsIframeLoading(false);
      setIframeUrl(null);
      return;
    }
    if (iframeUrl || iframeError) {
      return;
    }
    setIsIframeLoading(true);
    setIframeUrl(url);
    setIframeHistory([url]);
    setCurrentHistoryIndex(0);
  }, [viewMode, url, iframeUrl, iframeError, embedSupport]);

  useLayoutEffect(() => {
    const previous = previousViewModeReference.current;
    if (previous !== viewMode) {
      previousViewModeReference.current = viewMode;
    }
    if (viewMode !== 'preview' || previous !== 'iframe') {
      return;
    }

    if (cleanupRafReference.current !== null) {
      cancelAnimationFrame(cleanupRafReference.current);
    }

    cleanupRafReference.current = requestAnimationFrame(() => {
      cleanupRafReference.current = null;
      setIframeUrl(null);
      setIsIframeLoading(false);
      setIframeError(false);
      setCurrentIframeTitle('');
      setIframeHistory([]);
      setCurrentHistoryIndex(-1);
    });

    return () => {
      if (cleanupRafReference.current !== null) {
        cancelAnimationFrame(cleanupRafReference.current);
        cleanupRafReference.current = null;
      }
    };
  }, [viewMode]);

  useLayoutEffect(
    () => () => {
      probeCancelledReference.current = true;
      if (cleanupRafReference.current !== null) {
        cancelAnimationFrame(cleanupRafReference.current);
        cleanupRafReference.current = null;
      }
    },
    []
  );

  const triggerProbeIfNeeded = useCallback(() => {
    if (hasProbedReference.current) {
      return;
    }
    hasProbedReference.current = true;
    probeCancelledReference.current = false;
    const shouldProbe = !!url && !isExcluded && canEmbedHost(url);
    if (!shouldProbe) {
      setEmbedSupport('blocked');
      return;
    }
    const timeoutMs = isVitest() ? 150 : 1500;
    const probe = async () => {
      try {
        const res = await probeIframeEmbeddability(url, { timeoutMs });
        if (!probeCancelledReference.current) {
          setEmbedSupport(res.status);
        }
      } catch {
        if (!probeCancelledReference.current) {
          setEmbedSupport('error');
        }
      }
    };
    probe().catch((error: unknown) => {
      console.error('Failed to probe iframe embeddability', error);
    });
  }, [isExcluded, url]);

  const markImageError = useCallback(() => {
    setImageError(true);
  }, []);

  const openIframeView = useCallback(() => {
    setIsFullscreen(false);
    setViewMode('iframe');
  }, []);

  const closeIframeView = useCallback(() => {
    setViewMode('preview');
  }, []);

  const handleIframeBack = useCallback(() => {
    setCurrentHistoryIndex((index) => {
      if (index <= 0) {
        return index;
      }
      const nextIndex = index - 1;
      const target = getHistoryEntry(iframeHistoryReference.current, nextIndex);
      setIframeUrl(target);
      setIsIframeLoading(true);
      return nextIndex;
    });
  }, []);

  const handleIframeLoad = useCallback((title?: string) => {
    setIsIframeLoading(false);
    setIframeError(false);
    if (typeof title === 'string') {
      setCurrentIframeTitle(title);
    }
  }, []);

  const handleIframeError = useCallback(() => {
    setIsIframeLoading(false);
    setIframeError(true);
  }, []);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((previous) => !previous);
  }, []);

  return {
    viewMode,
    imageError,
    iframeUrl,
    iframeError,
    isIframeLoading,
    iframeHistory,
    currentHistoryIndex,
    isFullscreen,
    currentIframeTitle,
    embedSupport,
    markImageError,
    openIframeView,
    closeIframeView,
    handleIframeBack,
    handleIframeLoad,
    handleIframeError,
    toggleFullscreen,
    triggerProbeIfNeeded,
    applyEmbedStatus,
  };
}
